import type { PlanPeriod } from '@app/features/order/pages/TeamBillingOverview/types';
import authBase from '../authBase';
import type { FeatureCode, MetricsStatus, PlanEntitlement } from './enums';

export type PricingVersion = 'v1' | 'v2';
export type MetricPeriod = 'yearly' | 'monthly' | null;

export type PlanOverview = {
    plan: {
        id: number;
        active: boolean;
        invoiced: boolean;
        name: string;
        expirationDate: string;
        seatBased: boolean;
        period: PlanPeriod;
        periodCount: number;
        price: number;
        status: string | null;
        unpaidInvoiceUrl: string | null;
        start: string | null;
        end: string | null;
        daysLeft: number | null;
        canBeModified: boolean;
    };
    nextPlan: {
        name: string;
        seatBased: boolean;
        period: PlanPeriod;
        periodCount: number;
        seatLimit: number;
        scheduledPlanStartDate: string;
    } | null;
    exceededKeyCount: number | null;
    exceededKeyCost: number | null;
    exceededKeyMinutesSinceLastUpdate: number | null;
    disableSubscriptionFunctionality: boolean;
    teamCreditCents: number;
    pricingVersion: PricingVersion;
    gracePeriodSeats: number;
    keysInBranches: number;
    metabaseSiteUrl: string;
    metabaseUrl: string;
};

export type MetricInfo = {
    metricCode: PlanEntitlement;
    currentValue: number | null;
    limitValue: number | null;
    metricStatus: MetricsStatus;
    metricPeriod: MetricPeriod;
    retrievedAt: string;
};

export type BillingMetrics = {
    data: MetricInfo[];
    meta: {
        count: number;
        retrievedAt: string;
    };
};

export type FeatureInfo = {
    code: FeatureCode;
    isEnabled: boolean;
};

export type BillingFeatures = {
    data: {
        teamUuid: string;
        retrievedAt: string;
        features: FeatureInfo[];
    };
};

export const teamV2Api = authBase
    .enhanceEndpoints({
        addTagTypes: ['teamMemberProjectsV2'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getPlanOverview: builder.query<PlanOverview, { teamUuid: string }>({
                query: ({ teamUuid }) => ({
                    method: 'GET',
                    url: `/v1/teams/${teamUuid}/plan/overview`,
                }),
            }),
            getBillingMetrics: builder.query<BillingMetrics, { teamUuid: string; filterMetrics?: PlanEntitlement[] }>({
                query: ({ teamUuid, filterMetrics }) => ({
                    method: 'GET',
                    url: `/v1/teams/${teamUuid}/billing-metrics`,
                    // API expects query params in the format `filter-metricCode[]=keyLimit&filter-metricCode[]=projectLimit`
                    params: filterMetrics?.length
                        ? new URLSearchParams(filterMetrics.map((metric) => ['filter-metricCode[]', metric]))
                        : undefined,
                }),
            }),
            getBillingFeatures: builder.query<BillingFeatures, { teamUuid: string; filterNames?: FeatureCode[] }>({
                query: ({ teamUuid, filterNames }) => ({
                    method: 'GET',
                    url: `/v1/teams/${teamUuid}/billing-features`,
                    // API expects query params in the format `filter-name[]=live_edit&filter-name[]=project_automations`
                    params: filterNames?.length
                        ? new URLSearchParams(filterNames.map((name) => ['filter-name[]', name]))
                        : undefined,
                }),
            }),
            getPricingVersion: builder.query<{ version: PricingVersion }, { teamUuid: string }>({
                query: ({ teamUuid }) => ({
                    method: 'GET',
                    url: `/v1/teams/${teamUuid}/plan/version`,
                }),
            }),
        }),
    });
